import { useState } from 'react'

import { FeatureToggleValue } from '@ankor-io/common/types/system'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  Field,
  Fieldset,
  Input,
  Label,
  Radio,
  RadioField,
  RadioGroup,
  Text,
} from '@ankor-io/ui-components'

export interface CreateFeatureFlagDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (toggleName: string, toggleValue: FeatureToggleValue) => void
  isSubmitting?: boolean
  title?: string
  description?: string
  buttonText?: string
  /** Existing toggle names from the server (GET /toggles keys). Used to prevent duplicates. */
  existingToggleNames?: string[]
}

const TOGGLE_NAME_PATTERN = /^[A-Z][A-Z0-9_]*$/

export function CreateFeatureFlagDialog({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Add Feature Flag',
  description = 'Create a new global feature flag. Enter a name in UPPER_SNAKE_CASE (e.g. MY_NEW_FEATURE).',
  buttonText = 'Create',
  existingToggleNames = [],
}: CreateFeatureFlagDialogProps) {
  const [toggleNameInput, setToggleNameInput] = useState('')
  const [selectedToggleValue, setSelectedToggleValue] = useState<FeatureToggleValue>(FeatureToggleValue.ENABLED)

  const existingSet = new Set(existingToggleNames.map((n) => n.toUpperCase()))
  const trimmedName = toggleNameInput.trim()
  const normalizedName = trimmedName.toUpperCase().replace(/\s+/g, '_')
  const isDuplicate = trimmedName.length > 0 && existingSet.has(normalizedName)
  const isValidFormat = normalizedName.length > 0 && TOGGLE_NAME_PATTERN.test(normalizedName)
  const canSubmit = isValidFormat && !isDuplicate && !isSubmitting

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit(normalizedName, selectedToggleValue)
    setToggleNameInput('')
    setSelectedToggleValue(FeatureToggleValue.ENABLED)
  }

  const handleClose = () => {
    setToggleNameInput('')
    setSelectedToggleValue(FeatureToggleValue.ENABLED)
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="md"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
      <DialogBody>
        <Fieldset>
          <Field>
            <Label>Feature flag name</Label>
            <Input
              type="text"
              value={toggleNameInput}
              onChange={(e) => setToggleNameInput(e.target.value)}
              placeholder="e.g. MY_NEW_FEATURE"
              aria-invalid={trimmedName.length > 0 && (!isValidFormat || isDuplicate)}
              aria-describedby={isDuplicate ? 'duplicate-error' : undefined}
            />
            {trimmedName.length > 0 && !TOGGLE_NAME_PATTERN.test(normalizedName) && (
              <Text
                id="format-hint"
                className="mt-1 text-sm text-amber-600 dark:text-amber-400"
              >
                Use UPPER_SNAKE_CASE (letters, numbers, underscores only; must start with a letter).
              </Text>
            )}
            {isDuplicate && (
              <Text
                id="duplicate-error"
                className="mt-1 text-sm text-red-600 dark:text-red-400"
              >
                A feature flag with this name already exists.
              </Text>
            )}
          </Field>

          <Field>
            <Label>Initial value</Label>
            <RadioGroup
              value={selectedToggleValue}
              onChange={(value) => setSelectedToggleValue(value as FeatureToggleValue)}
            >
              <RadioField>
                <Radio value={FeatureToggleValue.ENABLED} />
                <Label>Enabled</Label>
              </RadioField>
              <RadioField>
                <Radio value={FeatureToggleValue.DISABLED} />
                <Label>Disabled</Label>
              </RadioField>
            </RadioGroup>
          </Field>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button
          plain
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
